import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import ProfileSidebar from '../../components/ProfileSidebar/ProfileSidebar';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import { useClickOutside } from '../../hooks';
import { useNotifications } from '../../hooks/useNotifications';
import notificationService from '../../../services/notificationService';

// Action types for better maintainability
const ACTION_TYPES = {
  DELETE: 'Delete',
  MARK_AS_READ: 'Mark As Read',
  MARK_AS_UNREAD: 'Mark As Unread'
};

const MessagesPage = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const {
    notifications,
    loading,
    pagination,
    handleMarkAllAsRead,
    handleDeleteNotification,
    handleDeleteAll,
    handleBulkAction,
    refreshNotifications,
    unreadCount
  } = useNotifications(currentPage, 10, showUnreadOnly ? 'unread' : 'all');

  const [selectedIds, setSelectedIds] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // 'all' or message id
  const [expandedNotifications, setExpandedNotifications] = useState({});

  // Use custom hook for click outside detection
  const dropdownRef = useClickOutside(() => setShowActionDropdown(false));

  // Handle notification link clicks
  useEffect(() => {
    const handleLinkClick = (e) => {
      const link = e.target.closest('a[data-link]');
      if (!link) return;

      e.preventDefault();
      const dataLink = link.getAttribute('data-link');
      
      if (!dataLink) return;

      // Handle relative paths (e.g., /jetsetter/profile/user-details)
      if (dataLink.startsWith('/')) {
        navigate(dataLink);
      } 
      // Handle full URLs - extract path from URL
      else if (dataLink.startsWith('http')) {
        try {
          const url = new URL(dataLink);
          navigate(url.pathname);
        } catch (error) {
          console.error('Invalid URL:', dataLink);
        }
      }
    };

    if (contentRef.current) {
      contentRef.current.addEventListener('click', handleLinkClick);
      return () => {
        contentRef.current?.removeEventListener('click', handleLinkClick);
      };
    }
  }, [navigate]);

  // Fetch notifications when page changes
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    refreshNotifications(page);
    // Reset expanded states when moving to a new page
    setExpandedNotifications({});
    // Scroll to top of message list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [refreshNotifications]);

  // Transform notifications to match UI structure
  const messages = useMemo(() => {
    let filtered = notifications.map((notif, index) => ({
      ...notif,
      id: notif.id || `temp-${index}`,
      originalId: notif.id,
      title: notif.title,
      content: notif.content,
      date: notif.created_at,
      isRead: notif.read === 1,
      isSelected: selectedIds.includes(notif.id),
      formattedDate: new Date(notif.created_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }),
      formattedTime: new Date(notif.created_at).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }));

    return filtered;
  }, [notifications, selectedIds, showUnreadOnly]);

  // Toggle notification expansion and mark as read
  const handleToggleExpand = useCallback(async (id, isRead, originalId) => {
    setExpandedNotifications(prev => ({
      ...prev,
      [id]: !prev[id]
    }));

    // Only mark as read if we have a real ID (not a temp one) and it's unread
    if (originalId && !isRead && !expandedNotifications[id]) {
      try {
        await notificationService.markAsRead(originalId);
        refreshNotifications(currentPage);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  }, [currentPage, expandedNotifications, refreshNotifications]);

  // Memoized handlers for better performance
  const handleMarkAllAsReadClick = useCallback(() => {
    handleMarkAllAsRead();
  }, [handleMarkAllAsRead]);

  const handleShowOnlyUnread = useCallback(() => {
    setShowUnreadOnly(prev => !prev);
    setCurrentPage(1); // Reset to first page when changing filters
  }, []);

  const handleDeleteAllClick = useCallback(() => {
    setDeleteTarget('all');
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteAll = useCallback(async () => {
    await handleDeleteAll();
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setSelectedIds([]);
    refreshNotifications(currentPage);
  }, [handleDeleteAll, currentPage, refreshNotifications]);

  const handleMessageSelect = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.length === messages.length && messages.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(messages.map(msg => msg.originalId || msg.id));
    }
  }, [messages, selectedIds]);

  const handleDeleteMessageClick = useCallback((id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteMessage = useCallback(async () => {
    if (deleteTarget === 'selected') {
      await handleBulkAction('delete', selectedIds);
    } else if (deleteTarget && deleteTarget !== 'all') {
      await handleDeleteNotification(deleteTarget);
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setSelectedIds([]);
    refreshNotifications(currentPage);
  }, [currentPage, deleteTarget, selectedIds, handleDeleteNotification, handleBulkAction, refreshNotifications]);

  const handleActionSelect = useCallback((action) => {
    setShowActionDropdown(false);

    if (selectedIds.length === 0) {
      if (action === ACTION_TYPES.DELETE) {
        handleDeleteAllClick();
        return;
      }

      toast.error('Please select at least one message.');
      return;
    }

    switch (action) {
      case ACTION_TYPES.DELETE:
        setDeleteTarget('selected');
        setShowDeleteModal(true);
        break;

      case ACTION_TYPES.MARK_AS_READ:
        handleBulkAction('mark_read', selectedIds).then(() => {
          toast.success(`${selectedIds.length} message(s) marked as read.`);
          setSelectedIds([]);
          refreshNotifications(currentPage);
        });
        break;

      case ACTION_TYPES.MARK_AS_UNREAD:
        handleBulkAction('mark_unread', selectedIds).then(() => {
          toast.success(`${selectedIds.length} message(s) marked as unread.`);
          setSelectedIds([]);
          refreshNotifications(currentPage);
        });
        break;

      default:
        break;
    }
  }, [selectedIds, currentPage, handleDeleteAllClick, handleBulkAction, refreshNotifications]);

  const toggleDropdown = useCallback(() => {
    setShowActionDropdown(prev => !prev);
  }, []);

  // Computed values
  const selectedCount = selectedIds.length;
  const hasMessages = messages.length > 0;

  const handleConfirmDelete = useCallback(() => {
    if (deleteTarget === 'all') {
      confirmDeleteAll();
    } else {
      confirmDeleteMessage();
    }
  }, [deleteTarget, confirmDeleteAll, confirmDeleteMessage]);

  return (
    <div className="bg-jet-gray-100 pt-[40px] pb-8 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        <ProfileSidebar />

        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          <div className="bg-jet-yellow px-5 py-3 border-b border-yellow-200 rounded-t-[calc(0.25rem-1px)] flex items-center justify-center">
            <h3 className="text-[18px] font-semibold text-[#000] m-0">Messages</h3>
          </div>

          <div className="p-3 sm:p-5">
            <MessagesActionBar
              dropdownRef={dropdownRef}
              showActionDropdown={showActionDropdown}
              onToggleDropdown={toggleDropdown}
              onActionSelect={handleActionSelect}
              onDeleteAll={handleDeleteAllClick}
              onMarkAllAsRead={handleMarkAllAsReadClick}
              onShowOnlyUnread={handleShowOnlyUnread}
              showUnreadOnly={showUnreadOnly}
              onSelectAll={handleSelectAll}
              isAllSelected={messages.length > 0 && selectedIds.length === messages.length}
              selectedCount={selectedCount}
            />

            <MessagesList
              ref={contentRef}
              messages={messages}
              loading={loading}
              onMessageSelect={handleMessageSelect}
              onDeleteMessage={handleDeleteMessageClick}
              onToggleExpand={handleToggleExpand}
              expandedNotifications={expandedNotifications}
            />

            {pagination && (showUnreadOnly ? unreadCount > 10 : pagination.totalPages > 1) && (
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}

            {selectedCount > 0 && (
              <SelectionInfo count={selectedCount} />
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        type="delete"
        title="Delete Message(s)?"
        message={deleteTarget === 'all'
          ? "You are about to delete all selected message(s). Click \"DELETE\" to proceed or \"CANCEL\" to exit."
          : deleteTarget === 'selected'
            ? `You are about to delete ${selectedIds.length} selected message(s). Click "DELETE" to proceed or "CANCEL" to exit.`
            : "You are about to delete this message. Click \"DELETE\" to proceed or \"CANCEL\" to exit."
        }
        theme="modern"
      />
    </div>
  );
};

// Extracted components for better maintainability
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages + 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 4) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex justify-start items-center gap-1 mt-4 mb-2 px-5">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <i className="fas fa-chevron-left text-[10px]"></i>
      </button>

      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          className={`w-8 h-8 flex items-center justify-center border text-sm transition-all duration-200 rounded ${page === currentPage
            ? 'bg-jet-yellow border-jet-yellow text-white font-bold'
            : page === '...'
              ? 'border-transparent cursor-default'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <i className="fas fa-chevron-right text-[10px]"></i>
      </button>
    </div>
  );
};

// Extracted components for better maintainability
const MessagesActionBar = ({
  dropdownRef,
  showActionDropdown,
  onToggleDropdown,
  onActionSelect,
  onDeleteAll,
  onMarkAllAsRead,
  onShowOnlyUnread,
  showUnreadOnly,
  onSelectAll,
  isAllSelected,
  selectedCount
}) => (
  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center py-3.5 px-5 bg-white border-b border-jet-gray-200 gap-3 sm:gap-4 min-h-14">
    <div className="flex items-center gap-4">
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={onSelectAll}
          className="w-4 h-4 cursor-pointer"
          title="Select all on this page"
        />
      </div>
      <div className="relative inline-block" ref={dropdownRef}>
        <button
          className="bg-jet-yellow text-jet-gray-800 border-0 px-4 py-2 rounded text-sm font-medium cursor-pointer flex items-center gap-2 min-w-[120px] justify-between transition-colors duration-200 hover:bg-jet-yellow-hover w-full sm:w-auto"
          onClick={onToggleDropdown}
          aria-expanded={showActionDropdown}
          aria-haspopup="true"
        >
          Select Action
          <span aria-hidden="true">▼</span>
        </button>
        {showActionDropdown && (
          <ActionDropdownMenu onActionSelect={onActionSelect} />
        )}
      </div>
    </div>

    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
      <button 
        disabled={selectedCount > 0}
        className={`bg-jet-yellow text-jet-gray-800 border-0 px-4 py-2 rounded text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
          selectedCount > 0 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-pointer hover:bg-jet-yellow-hover'
        }`} 
        onClick={onDeleteAll}
      >
        Delete All
      </button>
      <button 
        disabled={selectedCount > 0}
        className={`bg-jet-yellow text-jet-gray-800 border-0 px-4 py-2 rounded text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
          selectedCount > 0 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-pointer hover:bg-jet-yellow-hover'
        }`} 
        onClick={onMarkAllAsRead}
      >
        Mark All As Read
      </button>
      <button 
        disabled={selectedCount > 0}
        className={`bg-jet-yellow text-jet-gray-800 border-0 px-4 py-2 rounded text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
          selectedCount > 0 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-pointer hover:bg-jet-yellow-hover'
        }`} 
        onClick={onShowOnlyUnread}
      >
        {showUnreadOnly ? 'Show All' : 'Show Only Unread'}
      </button>
    </div>
  </div>
);

const ActionDropdownMenu = ({ onActionSelect }) => (
  <div className="absolute top-full left-0 bg-[#ffeb00] border border-[#ddd] rounded shadow-[0_4px_12px_rgba(0,0,0,0.15)] z-10 min-w-[150px] mt-[5px] overflow-hidden">

    <div
      onClick={() => onActionSelect(ACTION_TYPES.DELETE)}
      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold hover:bg-[#e6d300] flex items-center gap-2"
    >
      <span className="text-[12px]">✓</span> Delete
    </div>

    <div
      onClick={() => onActionSelect(ACTION_TYPES.MARK_AS_READ)}
      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold hover:bg-[#e6d300] flex items-center gap-2"
    >
      <span className="text-[12px]">✓</span> Mark As Read
    </div>

    <div
      onClick={() => onActionSelect(ACTION_TYPES.MARK_AS_UNREAD)}
      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold hover:bg-[#e6d300] flex items-center gap-2"
    >
      <span className="text-[12px]">✓</span> Mark As Unread
    </div>

  </div>
);

const MessagesList = React.forwardRef(({ messages, loading, onMessageSelect, onDeleteMessage, onToggleExpand, expandedNotifications }, ref) => (
  <div className="bg-white pt-3" ref={ref}>
    {loading ? (
      <div className="py-8 px-5 text-center text-base text-jet-gray-500">
        Loading messages...
      </div>
    ) : messages.length === 0 ? (
      <EmptyState />
    ) : (
      messages.map((message) => (
        <MessageRow
          key={message.id}
          message={message}
          onSelect={onMessageSelect}
          onDelete={onDeleteMessage}
          onToggleExpand={onToggleExpand}
          isExpanded={expandedNotifications[message.id] || false}
        />
      ))
    )}
  </div>
));

const MessageRow = ({ message, onSelect, onDelete, onToggleExpand, isExpanded }) => (
  <div className="mb-3 flex items-start gap-3">
    <div className={`flex-1 border border-jet-gray-200 rounded-lg shadow-sm transition-all duration-200 ${!message.isRead ? 'bg-blue-50' : 'bg-white'} hover:shadow-md`}>
      {/* Title row */}
      <div className="flex items-center py-3 px-4 gap-4 min-h-12">
        <div className="flex-shrink-0">
          <input
            type="checkbox"
            checked={message.isSelected}
            onChange={() => onSelect(message.originalId || message.id)}
            aria-label={`Select message: ${message.title}`}
            className="w-4 h-4 cursor-pointer"
          />
        </div>

        <div
          className="flex-1 min-w-0 text-sm text-jet-gray-800 font-semibold cursor-pointer"
          onClick={() => onToggleExpand(message.id, message.isRead, message.originalId)}
        >
          {message.title}
        </div>
      </div>

      {/* Slide-down content */}
      <div
        className="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out"
        style={{
          gridTemplateRows: isExpanded ? '1fr' : '0fr',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-3">
            <div className="text-sm text-jet-gray-600 font-semibold mb-2">
              {message.formattedDate}, {message.formattedTime}
            </div>
            <div
              className="text-sm text-jet-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: message.content }}
            />
          </div>
        </div>
      </div>
    </div>

    {/* Delete button outside the box - fixed height, aligned to top */}
    <div className="flex-shrink-0">
      <button
        className="bg-jet-red-500 text-white border-0 py-3 px-3.5 rounded cursor-pointer transition-colors duration-200 flex items-center justify-center hover:bg-jet-red-600 h-12"
        onClick={() => onDelete(message.originalId || message.id)}
        aria-label={`Delete message: ${message.title}`}
      >
        <FontAwesomeIcon icon={faTrashAlt} style={{ color: 'white', fontSize: '15px' }} />
      </button>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="py-8 px-5 text-center text-base text-jet-gray-500">
    No messages found
  </div>
);

const SelectionInfo = ({ count }) => (
  <div className="selection-info">
    {count} message{count !== 1 ? 's' : ''} selected
  </div>
);

export default MessagesPage;